import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { apiFetch } from "../../api/client";
import BirthDateField from "../../components/auth/BirthDateField";
import PolicyModal from "../../components/auth/PolicyModal";
import { PRIVACY_POLICY } from "../../data/legalDocuments";

const buttonClass = "min-h-12 rounded-lg bg-blue-950 px-5 py-3 font-semibold text-white disabled:opacity-50 focus-visible:ring-2 focus-visible:ring-blue-600";

export default function GuardianVerificationRoute() {
  const [params] = useSearchParams();
  const [token] = useState(() => params.get("token") || "");
  const navigate = useNavigate();
  const location = useLocation();
  const busy = useRef(false);
  const [state, setState] = useState("loading");
  const [data, setData] = useState(null);
  const [number, setNumber] = useState("");
  const [date, setDate] = useState("");
  const [authorized, setAuthorized] = useState(false);
  const [message, setMessage] = useState("");
  const [policy, setPolicy] = useState(false);
  const [dateKey, setDateKey] = useState(0);

  useLayoutEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "referrer";
    meta.content = "no-referrer";
    document.head.appendChild(meta);
    // Router replacement preserves both BrowserRouter and native HashRouter.
    navigate({ pathname: location.pathname, search: "", hash: "" }, { replace: true });
    return () => meta.remove();
  }, [navigate, location.pathname]);

  const handleError = (error) => {
    setData(error.payload || null);
    if (error.code === "token_expired") setState("expired");
    else if (["invalid_token", "validation_error"].includes(error.code)) setState("invalid");
    else if (error.code === "attempt_limit" || error.payload?.state === "locked") setState("locked");
    else if (error.status === 422) setState("mismatch");
    else setState("unavailable");
  };

  useEffect(() => {
    let active = true;
    if (!/^[a-f0-9]{64}$/.test(token)) { setState("invalid"); return; }
    apiFetch("/guardian/verification", { auth: false, query: { token } })
      .then(result => {
        if (!active) return;
        setData(result);
        setState(result.state === "locked" ? "locked" : "ready");
      })
      .catch(error => { if (active) handleError(error); });
    return () => { active = false; };
  }, [token]);

  const submit = async (event) => {
    event.preventDefault();
    if (busy.current || state === "verified" || state === "locked") return;
    if (!/^[1-9][0-9]{4,11}$/.test(number) || !date || !authorized) {
      setMessage("Introduce una cédula de 5 a 12 dígitos, sin cero inicial, una fecha válida y confirma la autorización.");
      return;
    }
    busy.current = true;
    setState("submitting");
    setMessage("");
    try {
      const result = await apiFetch("/guardian/verification", {
        method: "POST", auth: false, timeoutMs: 150_000,
        body: { token, document_number: number, issue_date: date, authorization: true },
      });
      setData(result);
      setState("verified");
    } catch (error) { handleError(error); }
    finally {
      setNumber("");
      setDate("");
      setAuthorized(false);
      setDateKey(key => key + 1);
      busy.current = false;
    }
  };

  const formVisible = ["ready", "mismatch", "unavailable", "submitting"].includes(state);
  return <main className="min-h-screen bg-gray-50 px-4 py-8 sm:py-12">
    <section className="mx-auto w-full max-w-lg rounded-xl bg-white p-5 sm:p-8 shadow-sm space-y-6">
      <h1 className="text-2xl font-bold text-blue-950">{state === "verified" ? "Identidad verificada" : "Autorización del tutor legal"}</h1>
      <div role="status" aria-live="polite" className="space-y-2 text-gray-700">
        {state === "loading" && <p>Cargando solicitud…</p>}
        {state === "verified" && <p>Gracias. Tu autorización quedó registrada y el deportista ya puede continuar en Athletain. Puedes cerrar esta página.</p>}
        {state === "expired" && <p>Enlace expirado. Pide al deportista que solicite un nuevo enlace desde su cuenta.</p>}
        {state === "invalid" && <p>Este enlace es inválido o ya fue utilizado. Pide al deportista que solicite otro enlace.</p>}
        {state === "locked" && <p>Se alcanzó el límite de intentos. {data?.retry_at ? `Puedes solicitar un nuevo enlace después del ${new Date(data.retry_at).toLocaleString("es-CO")}.` : "Espera 24 horas y pide al deportista un nuevo enlace."}</p>}
        {state === "mismatch" && <p>No pudimos verificar los datos. Revisa tu cédula y fecha de expedición. Puedes intentarlo de nuevo o contactar al soporte de Athletain si el problema continúa.</p>}
        {state === "unavailable" && <p>La verificación no está disponible temporalmente. Inténtalo más tarde.</p>}
        {state === "submitting" && <p>Verificando identidad… Puede tardar hasta dos minutos y medio. Mantén esta página abierta.</p>}
      </div>
      {formVisible && Number.isInteger(data?.attempts_remaining) && <p className="text-sm text-gray-700">Intentos disponibles: {data.attempts_remaining}</p>}
      {formVisible && <form aria-label="Verificación de identidad del tutor" onSubmit={submit} className="space-y-5" autoComplete="off">
        <p className="text-gray-700">Para autorizar el uso de Athletain por {data?.athlete_name || "el deportista menor de edad"}, necesitamos comprobar tu identidad como tutor legal. No necesitas una cuenta.</p>
        <fieldset disabled={state === "submitting"} className="space-y-5">
          <label className="block text-sm font-medium text-gray-800" htmlFor="guardian-document">Número de cédula de ciudadanía
            <input id="guardian-document" value={number} onChange={event => setNumber(event.target.value.replace(/\D/g, "").slice(0, 12))} inputMode="numeric" type="text" autoComplete="off" minLength={5} maxLength={12} pattern="[1-9][0-9]{4,11}" required className="mt-2 w-full min-h-12 rounded-lg border border-gray-200 bg-gray-100 p-3 text-base focus:ring-2 focus:ring-blue-900" />
          </label>
          <BirthDateField key={dateKey} value={date} onChange={setDate} label="Fecha de expedición de tu cédula*" autoComplete={false} />
          <p className="text-sm text-gray-700">Usa la fecha de expedición impresa en tu cédula. La fecha de nacimiento no sirve para esta verificación.</p>
          <p className="text-sm text-gray-700">Usaremos tu cédula y fecha de expedición para verificar tu identidad con fuentes oficiales mediante nuestro proveedor de verificación. No guardamos el número completo ni la fecha enviada. Consulta la <button type="button" className="min-h-11 underline text-blue-950" onClick={() => setPolicy(true)}>Política de Privacidad</button>.</p>
          <label className="flex items-start gap-3 text-sm text-gray-800">
            <input type="checkbox" required checked={authorized} onChange={event => setAuthorized(event.target.checked)} className="mt-1 h-5 w-5 shrink-0 accent-blue-950" />
            Confirmo que soy su tutor legal y autorizo el tratamiento de sus datos y el uso de Athletain
          </label>
          {message && <p role="alert" className="text-sm text-red-700">{message}</p>}
          <button className={buttonClass} type="submit">{state === "submitting" ? "Verificando…" : "Verificar y autorizar"}</button>
        </fieldset>
      </form>}
      <PolicyModal isOpen={policy} onClose={() => setPolicy(false)} document={PRIVACY_POLICY} />
    </section>
  </main>;
}
